import type {AnalysisResult, ConversionSettings, FileUploadResponse} from "@/generated";

const VALID_EXTENSIONS = ['.mxl', '.xml', '.musicxml', '.mid', '.midi', '.pdf'];
const PDF_EXTENSIONS = ['.pdf'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

/** Ein Movement eines mehrteiligen PDF-Uploads mit eigener (client-)Lineage. */
export interface PdfMovementDescriptor {
    /** Server-``file_id`` des Movements (opakes Label). */
    fileId: string;
    /** Clientseitige Lineage-Id (eigene Bearbeitungslinie je Movement). */
    lineageId: string;
    filename: string;
    musicXml: string;
    /** 0-basierter Index im Dokument. */
    movementIndex: number;
}

/** Absicht eines Uploads – bestimmt, wie die Dateihistorie den neuen Stand verbucht. */
export type UploadIntent = 'fresh' | 'edit' | 'shift' | 'merge' | 'restore';
/**
 * Explizites Capture-Event für die Dateihistorie: Absicht + Daten wechseln **atomar** (statt
 * eines separat mutierten `lastUploadKind`-Flags + nachträglicher Rekonstruktion in Watchern).
 * `restore`-Uploads erzeugen bewusst KEIN Event (nur ein bereits vorhandener Stand wird neu
 * geladen).
 */
export type HistoryCaptureEvent =
    | { kind: 'upload'; lineageId: string; file: File; filename: string; fileType: string | null }
    | { kind: 'pdf-upload'; lineageId: string; filename: string; musicXml: string; fileType: string | null }
    | {
    kind: 'edited' | 'shifted' | 'merged' | 'mode-changed' | 'rebarred' | 'transposed';
    lineageId: string;
    filename: string;
    musicXml: string;
    semitones: number;
    fileType: string | null;
}
    | { kind: 'document'; documentId: string; activeLineageId: string; movements: PdfMovementDescriptor[] }
    | {
    kind: 'converted';
    lineageId: string;
    baseFilename: string;
    musicXml: string;
    conversionId: string | null;
    semitones: number;
    analysis: AnalysisResult | null;
    /** Snapshot der tatsächlich verwendeten Konvertierungs-Settings zum EMIT-Zeitpunkt (der
     *  Konsument liest sie nicht mehr live aus dem Settings-Store – der Stand könnte sich bis
     *  zur Queue-Verarbeitung geändert haben). */
    settings: ConversionSettings;
}
    | { kind: 'analysis'; lineageId: string; semitones: number; analysis: AnalysisResult };

// ============ Constants ============

export function newLineageId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
    return `lineage-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// ============ Helpers ============

function getFileExtension(filename: string): string {
    const parts = filename.split('.');
    return parts.length > 1 ? '.' + parts.pop()!.toLowerCase() : '';
}

// Pure Ableitung – der frühere `switchToInputTab()`-Nebeneffekt liegt jetzt im
// `[fileInfo, selectedFileContent]`-Watcher des useTabStore.
export function createFileInfo(file: File): FileUploadResponse {
    return {
        file_size: file.size,
        filename: file.name,
        file_id: '',
        file_type: file.type,
    };
}

export function isPdfFile(file: File): boolean {
    return PDF_EXTENSIONS.includes(getFileExtension(file.name));
}

// ============ Store Definition ============
export function validateFile(file: FileUploadResponse): string | null {
    const extension = getFileExtension(file.filename ?? '');

    if (!VALID_EXTENSIONS.includes(extension)) {
        return 'Ungültiger Dateityp. Erlaubt sind MusicXML- oder MIDI-Dateien.';
    }

    if (file.file_size && file.file_size > MAX_SIZE_BYTES) {
        return 'Datei zu groß. Maximal 10MB erlaubt.';
    }

    return null;
}